/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/generate/GenerateYearMonthId.ts
 * @create:      2025-06-28 09:39:51.336
 * @modify:      2025-07-11 21:19:12.891
 * @version:     6.0.0
 * @times:       11
 * @lines:       67
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { FormatCurrentDate } from "../date/FormatCurrentDate";
import { DateTimeFormat } from "../enum/DateTimeFormat";
import { GenerateNumberString } from "./GenerateNumberString";

/**
 * Generates a unique identifier string consisting of the current year and month,
 * followed by a random numeric string of specified length. Optionally, a validation
 * function can be provided to ensure the generated ID meets custom criteria.
 *
 * @param randomLength - The length of the random numeric string to append after the year and month. Defaults to 4.
 * @param validFunc - An optional asynchronous function that takes the generated ID as input and returns a boolean indicating its validity.
 * @returns A promise that resolves to a unique identifier string in the format `YYYYMM####`, where `####` is a random number of the specified length.
 * 
 * @since 4.0.0
 * @version 2021-12-11
 */
export async function GenerateYearMonthId(randomLength = 4, validFunc?: (id: string) => Promise<boolean>) {
    const ym = FormatCurrentDate(DateTimeFormat.YearMonth, 0);

    const generate = () => {
        const randomNumberStr = GenerateNumberString(randomLength);
        return ym + randomNumberStr;
    };

    let id = generate();

    if (validFunc !== undefined) {
        while (!await validFunc(id)) {
            id = generate();
        }
    }

    return id;
}
